import { LegSelectionItem } from '@/components/purchase/checkout/seat-selection/modal/LegSelectionItem';
import { useSeatSelection } from '@/hooks/useSeatSelection';
import { useSeatSelectionLegs } from '@/hooks/useSeatSelectionLegs';
import { TransText } from '@/i18n/trans/text';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import {
  Button,
  Icons,
  Popover,
  RadioGroup,
  Typography,
} from '@packages/webshop-design-system';
import type { FC, ReactElement } from 'react';
import { useMediaQuery } from 'react-responsive';

export const LegSelectionBanner: FC = (): ReactElement => {
  const { selectedLegId, handleLegSelect } = useSeatSelection();
  const { legs } = useSeatSelectionLegs();
  const selectedLegInfo = legs.find((leg) => leg.id === selectedLegId);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  return (
    <div className="border-y border-primary bg-primary-light px-6 py-2">
      <div className="relative flex justify-start laptop:justify-center">
        <div className="mt-1 flex w-[90%] flex-col items-start laptop:items-center">
          <div className="flex w-fit flex-row items-center gap-1">
            <Icons.nonStop width={12} height={12} className="text-neutral" />
            <Typography variant="label" className="text-neutral">
              <span className="font-bold">
                <TransText i18nKey="leg" /> {selectedLegInfo?.legNumber}
                {': '}
              </span>
              {selectedLegInfo?.direction && (
                <TransText i18nKey={selectedLegInfo.direction} />
              )}
            </Typography>
          </div>
          {selectedLegInfo && (
            <div className="mt-1 flex w-full items-center justify-start gap-1 laptop:justify-center">
              <Typography
                variant="subtitle"
                className="max-w-[250px] truncate text-dark"
              >
                {selectedLegInfo.origin}
              </Typography>
              <Icons.oneWay
                width={16}
                height={16}
                className="shrink-0 text-neutral"
              />
              <Typography
                variant="subtitle"
                className="max-w-[250px] truncate text-dark"
              >
                {selectedLegInfo.destination}
              </Typography>
            </div>
          )}
        </div>

        <div className="absolute -right-3 -top-2">
          <Popover
            trigger={
              <Button
                data-testid="language-select-trigger"
                variant="ghostLink"
                className="group text-primary"
                size="small"
              >
                <TransText i18nKey="legs" />
                <Icons.chevronDown
                  height={16}
                  width={16}
                  className="transition-transform duration-200 group-aria-expanded:rotate-180"
                />
              </Button>
            }
            content={
              <RadioGroup
                value={selectedLegId ?? ''}
                onChange={handleLegSelect}
              >
                {legs.map((leg) => (
                  <LegSelectionItem
                    key={leg.id}
                    leg={leg}
                    isSelected={selectedLegId === leg.id}
                    onSelect={handleLegSelect}
                  />
                ))}
              </RadioGroup>
            }
            placement="bottom end"
            offset={isLaptopOrBigger ? 25 : 23}
            data-testid="leg-selection-popover"
            className={{
              root: cn('min-w-[320px] border-neutral-light', {
                'w-screen rounded-none border-b-0 border-l-0 border-r-0 border-t-primary bg-white':
                  !isLaptopOrBigger,
              }),
              content: 'p-4',
            }}
          />
        </div>
      </div>
    </div>
  );
};
